import axiosInstance from '@/lib/enhancedAxiosInstance';
import { config } from '@/config/config';
import { logError, logDebug, logWarn } from '@/utils/errorHandler';

// ============================================================================
// ENTERPRISE ANALYTICS SERVICE
// ============================================================================

const ANALYTICS_URL = '/api/analytics';

// ============================================================================
// TYPE DEFINITIONS
// ============================================================================

export interface CompletenessReport {
  overall: number;
  total_attributes: number;
  completed_attributes: number;
  byAttribute: Array<{
    name: string;
    completed: number;
    total: number;
  }>;
  byCategory: Array<{
    name: string;
    value: number;
  }>;
  isNewOrganization?: boolean;
  message?: string;
}

export interface ReadinessReport {
  channel: string;
  readiness_percentage: number;
  total_attributes: number;
  completed_attributes: number;
  channel_breakdown?: Array<{
    name: string;
    value: number;
  }>;
  field_breakdown?: Array<{
    name: string;
    completed: number;
    missing: number;
  }>;
}

export interface LocalizationQualityReport {
  locale: string;
  translation_coverage: number;
  total_attributes: number;
  translated_attributes: number;
  quality_score?: number;
  pending_translations?: number;
}

export interface EnrichmentVelocityReport {
  date: string;
  edit_count: number;
  avg_time_to_publish: number;
  products_enriched: number;
  velocity_trend: Array<{
    date: string;
    edit_count: number;
    avg_time_to_publish: number;
    products_enriched: number;
  }>;
  top_editors: Array<{
    user: string;
    edit_count: number;
  }>;
}

export interface ChangeHistoryReport {
  id: number;
  date: string;
  username: string;
  entity_type: string;
  entity_id: number;
  action: string;
  details: string;
  payload?: any; // Optional payload with detailed change information
}

export interface AnalyticsError {
  error: string;
  detail: string;
  timestamp: string;
}

// ============================================================================
// ANALYTICS SERVICE FUNCTIONS
// ============================================================================

/**
 * Get completeness report data from analytics API
 *
 * Returns comprehensive completeness metrics including:
 * - Overall completeness percentage
 * - Attribute-level breakdown with completion stats
 * - Category-level breakdown with completion percentages
 */
export const getCompletenessReport = async (): Promise<CompletenessReport> => {
  try {
    if (config.debug.enableLogs) {
      logDebug('Analytics API Call: Completeness Report', {
        url: `${ANALYTICS_URL}/completeness/`,
        timestamp: new Date().toISOString(),
      });
    }

    const response = await axiosInstance.get(`${ANALYTICS_URL}/completeness/`);

    if (config.debug.enableLogs) {
      logDebug('Analytics API Response: Completeness Report', {
        status: response.status,
        dataKeys: Object.keys(response.data),
        overall: response.data.overall,
        attributeCount: response.data.byAttribute?.length,
        categoryCount: response.data.byCategory?.length,
      });
    }

    return response.data;
  } catch (error) {
    logError(
      'Failed to fetch completeness report',
      {
        component: 'analyticsService',
        action: 'getCompletenessReport',
        metadata: { error },
      },
      { showToast: false, userFacing: false }
    );
    throw error;
  }
};

/**
 * Get readiness report data from analytics API
 *
 * Returns channel readiness metrics including:
 * - Channel-specific readiness percentage
 * - Total and completed attributes count
 * - Channel breakdown and field-level details
 */
export const getReadinessReport = async (channel: string = 'default'): Promise<ReadinessReport> => {
  try {
    const url = `${ANALYTICS_URL}/readiness/?channel=${encodeURIComponent(channel)}`;

    if (config.debug.enableLogs) {
      logDebug('Analytics API Call: Readiness Report', {
        url,
        channel,
        timestamp: new Date().toISOString(),
      });
    }

    const response = await axiosInstance.get(url);

    if (config.debug.enableLogs) {
      logDebug('Analytics API Response: Readiness Report', {
        status: response.status,
        channel: response.data.channel,
        readiness: response.data.readiness_percentage,
        totalAttributes: response.data.total_attributes,
      });
    }

    return response.data;
  } catch (error) {
    logError(
      'Failed to fetch readiness report',
      {
        component: 'analyticsService',
        action: 'getReadinessReport',
        metadata: { channel, error },
      },
      { showToast: false, userFacing: false }
    );
    throw error;
  }
};

/**
 * Get localization quality report data from analytics API
 *
 * Returns localization coverage statistics for different locales:
 * - Translation coverage percentage per locale
 * - Total and translated attributes count
 * - Quality scores and pending translations
 */
export const getLocalizationQualityReport = async (): Promise<LocalizationQualityReport[]> => {
  try {
    if (config.debug.enableLogs) {
      logDebug('Analytics API Call: Localization Quality Report', {
        url: `${ANALYTICS_URL}/localization-quality/`,
        timestamp: new Date().toISOString(),
      });
    }

    const response = await axiosInstance.get(`${ANALYTICS_URL}/localization-quality/`);

    if (config.debug.enableLogs) {
      logDebug('Analytics API Response: Localization Quality Report', {
        status: response.status,
        localeCount: response.data?.length,
        locales: response.data?.map((item: any) => item.locale),
      });
    }

    return response.data;
  } catch (error) {
    logError(
      'Failed to fetch localization quality report',
      {
        component: 'analyticsService',
        action: 'getLocalizationQualityReport',
        metadata: { error },
      },
      { showToast: false, userFacing: false }
    );
    throw error;
  }
};

/**
 * Get enrichment velocity report data from analytics API
 *
 * Returns enrichment velocity metrics:
 * - Edit count and average time to publish
 * - Products enriched count
 * - Velocity trends and top editors
 */
export const getEnrichmentVelocityReport = async (): Promise<EnrichmentVelocityReport> => {
  try {
    if (config.debug.enableLogs) {
      logDebug('Analytics API Call: Enrichment Velocity Report', {
        url: `${ANALYTICS_URL}/enrichment-velocity/`,
        timestamp: new Date().toISOString(),
      });
    }

    const response = await axiosInstance.get(`${ANALYTICS_URL}/enrichment-velocity/`);

    if (config.debug.enableLogs) {
      logDebug('Analytics API Response: Enrichment Velocity Report', {
        status: response.status,
        editCount: response.data.edit_count,
        productsEnriched: response.data.products_enriched,
        avgTimeToPublish: response.data.avg_time_to_publish,
      });
    }

    return response.data;
  } catch (error) {
    logError(
      'Failed to fetch enrichment velocity report',
      {
        component: 'analyticsService',
        action: 'getEnrichmentVelocityReport',
        metadata: { error },
      },
      { showToast: false, userFacing: false }
    );
    throw error;
  }
};

/**
 * Get change history report data from analytics API
 *
 * Returns historical change tracking data with filtering support:
 * - Date range filtering
 * - User filtering
 * - Entity type filtering
 * - Action filtering
 */
export const getChangeHistoryReport = async (params?: {
  from?: string;
  to?: string;
  username?: string[];
  entity?: string[];
  action?: string[];
  limit?: number;
}): Promise<ChangeHistoryReport[]> => {
  try {
    const searchParams = new URLSearchParams();

    if (params?.from) searchParams.append('from', params.from);
    if (params?.to) searchParams.append('to', params.to);
    if (params?.username) params.username.forEach(u => searchParams.append('username', u));
    if (params?.entity) params.entity.forEach(e => searchParams.append('entity', e));
    if (params?.action) params.action.forEach(a => searchParams.append('action', a));
    if (params?.limit) searchParams.append('limit', params.limit.toString());

    const url = `${ANALYTICS_URL}/change-history/?${searchParams.toString()}`;

    if (config.debug.enableLogs) {
      logDebug('Analytics API Call: Change History Report', {
        url,
        params,
        timestamp: new Date().toISOString(),
      });
    }

    const response = await axiosInstance.get(url);

    if (config.debug.enableLogs) {
      logDebug('Analytics API Response: Change History Report', {
        status: response.status,
        recordCount: response.data?.length,
        dateRange: params?.from && params?.to ? `${params.from} to ${params.to}` : 'all',
      });
    }

    return response.data;
  } catch (error) {
    logError(
      'Failed to fetch change history report',
      {
        component: 'analyticsService',
        action: 'getChangeHistoryReport',
        metadata: { params, error },
      },
      { showToast: false, userFacing: false }
    );
    throw error;
  }
};

/**
 * Get analytics refresh status
 *
 * Returns information about the last analytics refresh:
 * - Refresh status and timing
 * - Data freshness indicators
 * - Health status
 */
export const getAnalyticsRefreshStatus = async () => {
  try {
    if (config.debug.enableLogs) {
      logDebug('Analytics API Call: Refresh Status', {
        url: `${ANALYTICS_URL}/refresh_status/`,
        timestamp: new Date().toISOString(),
      });
    }

    const response = await axiosInstance.get(`${ANALYTICS_URL}/refresh_status/`);

    if (config.debug.enableLogs) {
      logDebug('Analytics API Response: Refresh Status', {
        status: response.status,
        lastRefresh: response.data.last_refresh,
        healthStatus: response.data.health_status,
      });
    }

    return response.data;
  } catch (error) {
    logError(
      'Failed to fetch analytics refresh status',
      {
        component: 'analyticsService',
        action: 'getAnalyticsRefreshStatus',
        metadata: { error },
      },
      { showToast: false, userFacing: false }
    );
    throw error;
  }
};

/**
 * Get completeness report data with filters from dashboard API
 *
 * Returns comprehensive completeness metrics with filtering support:
 * - Overall completeness percentage
 * - Attribute-level breakdown with completion stats
 * - Category-level breakdown with completion percentages
 */
export const getCompletenessReportWithFilters = async (filters?: {
  locale?: string;
  category?: string;
  channel?: string;
  family?: string;
  from?: string;
  to?: string;
}): Promise<CompletenessReport> => {
  try {
    const params = new URLSearchParams();

    if (filters?.locale) params.append('locale', filters.locale);
    if (filters?.category) params.append('category', filters.category);
    if (filters?.channel) params.append('channel', filters.channel);
    if (filters?.family) params.append('family', filters.family);
    if (filters?.from) params.append('from_date', filters.from);
    if (filters?.to) params.append('to_date', filters.to);

    const url = `/api/dashboard/completeness-summary${params.toString() ? `?${params.toString()}` : ''}`;

    if (config.debug.enableLogs) {
      logDebug('Analytics API Call: Filtered Completeness Report', {
        url,
        filters,
        timestamp: new Date().toISOString(),
      });
    }

    const response = await axiosInstance.get(url);

    if (config.debug.enableLogs) {
      logDebug('Analytics API Response: Filtered Completeness Report', {
        status: response.status,
        dataKeys: Object.keys(response.data),
        overall: response.data.overall,
        attributeCount: response.data.byAttribute?.length,
        categoryCount: response.data.byCategory?.length,
      });
    }

    return response.data;
  } catch (error) {
    logError(
      'Failed to fetch filtered completeness report',
      {
        component: 'analyticsService',
        action: 'getCompletenessReportWithFilters',
        metadata: { filters, error },
      },
      { showToast: false, userFacing: false }
    );
    // SECURITY FIX: DO NOT FALLBACK TO OTHER ENDPOINTS
    // Return empty data for new organizations to prevent cross-organization data leaks
    logWarn('Returning empty data for new organization - no fallback to other endpoints', {
      component: 'analyticsService',
      action: 'getCompletenessReportWithFilters',
      metadata: { filters },
    });
    return {
      overall: 0,
      total_attributes: 0,
      completed_attributes: 0,
      byAttribute: [],
      byCategory: [],
      isNewOrganization: true,
      message: 'Analytics data will be available after the next scheduled refresh at 2 AM CET.',
    };
  }
};

/**
 * Get least completed attributes for locales
 *
 * Returns the top 5 least completed attributes for each locale or a specific locale:
 * - Attribute name and completion percentage
 * - Missing count and total count
 */
export const getLocaleAttributes = async (filters?: { locale?: string; family?: string }) => {
  try {
    const params = new URLSearchParams();
    if (filters?.locale) params.append('locale', filters.locale);
    if (filters?.family) {
      // Backend accepts both family and family_id for backward compatibility
      params.append('family', filters.family);
      params.append('family_id', filters.family);
    }

    const url = `${ANALYTICS_URL}/locale-attributes${params.toString() ? `?${params.toString()}` : ''}`;

    if (config.debug.enableLogs) {
      logDebug('Analytics API Call: Locale Attributes', {
        url,
        filters,
        timestamp: new Date().toISOString(),
      });
    }

    const response = await axiosInstance.get(url);

    if (config.debug.enableLogs) {
      logDebug('Analytics API Response: Locale Attributes', {
        status: response.status,
        localeCount: Object.keys(response.data).length,
        locales: Object.keys(response.data),
      });
    }

    return response.data;
  } catch (error) {
    logError(
      'Failed to fetch locale attributes',
      {
        component: 'analyticsService',
        action: 'getLocaleAttributes',
        metadata: { filters, error },
      },
      { showToast: false, userFacing: false }
    );
    throw error;
  }
};

// ============================================================================
// ERROR HANDLING UTILITIES
// ============================================================================

/**
 * Check if an error is an analytics error with structured data
 */
export const isAnalyticsError = (error: any): error is AnalyticsError => {
  return error && typeof error === 'object' && 'error' in error && 'detail' in error;
};

/**
 * Get user-friendly error message from analytics error
 */
export const getAnalyticsErrorMessage = (error: any): string => {
  if (isAnalyticsError(error)) {
    return error.detail || error.error || 'Analytics data temporarily unavailable';
  }

  if (error?.response?.data?.detail) {
    return error.response.data.detail;
  }

  if (error?.response?.data?.error) {
    return error.response.data.error;
  }

  if (error?.message) {
    return error.message;
  }

  return 'An unexpected error occurred while fetching analytics data';
};

// ============================================================================
// EXPORT ALL FUNCTIONS
// ============================================================================

export default {
  getCompletenessReport,
  getReadinessReport,
  getLocalizationQualityReport,
  getEnrichmentVelocityReport,
  getChangeHistoryReport,
  getAnalyticsRefreshStatus,
  isAnalyticsError,
  getAnalyticsErrorMessage,
  getCompletenessReportWithFilters,
  getLocaleAttributes,
};
